import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import api from "../utils/api";
import adminXB from "../assets/adminXB.png"; // Xbox logo
import adminPS from "../assets/adminPS.png"; // PlayStation logo
import adminPC from "../assets/adminPC.png"; // PC logo

const OrderInfo = () => {
  const { orderId } = useParams(); // Get orderId from route params
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Memoize static data (logos)
  const logos = useMemo(
    () => ({
      XB: adminXB,
      PS: adminPS,
      PC: adminPC,
    }),
    []
  );

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrderDetails(data);
      } catch (err) {
        setError("Failed to fetch order details. Please try again later.");
        console.error("Error fetching order details:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader" />
      </div>
    );

  if (error) return <p className="text-red-500 text-center">{error}</p>;

  const logo = logos[orderDetails.type] || adminPC;

  return (
    <div className="flex flex-col md:flex-row">
      {/* Sidebar */}
      <Sidebar />

      {/* Content */}
      <div className="flex-1 bg-gray-50 p-4 md:p-6">
        <h1 className="text-lg md:text-2xl font-bold text-gray-700 flex items-center">
          Order {orderDetails.orderId}
          <img src={logo} alt={orderDetails.type} className="ml-2 md:ml-4 w-6 md:w-8 h-6 md:h-8" />
        </h1>
        <p className="text-gray-500 mb-4 text-sm md:text-base">
          Dated: {new Date(orderDetails.createdAt).toLocaleDateString()}
        </p>

        {/* Status */}
        <div className="mb-6">
          <span
            className={`px-3 md:px-4 py-1 md:py-2 rounded-full text-xs md:text-sm font-medium ${orderDetails.status === "pending"
              ? "bg-purple-200 text-purple-700"
              : "bg-green-200 text-green-700"
              }`}
          >
            {orderDetails.status}
          </span>
        </div>

        {/* Order Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 bg-white p-4 md:p-6 rounded-lg shadow-md">
          {[
            { label: "Name", value: orderDetails.contactEmail },
            { label: "Quantity (K)", value: `${orderDetails.quantity}` },
            { label: "Price (USD)", value: `${orderDetails.priceInUSD}$` },
            {
              label: "Cashback Earned",
              value: `$${parseFloat(orderDetails.cashBack || 0).toFixed(2)}` // Round to 2 decimal places
            },
            { label: "Promo Code Applied", value: orderDetails.discountCode || "N/A" },
            {
              label: "Wallet credit used",
              value: `$${parseFloat(orderDetails.walletUsed || 0).toFixed(2)}` // Round to 2 decimal places
            },
            { label: "Contact Email", value: orderDetails.contactEmail },
            { label: "EA WebApp Email", value: orderDetails.originEmail },
            { label: "EA WebApp Password", value: orderDetails.originPassword },
            { label: "Backup Code 1", value: orderDetails.backupCode1 },
            { label: "Referral Code", value: orderDetails.referralCode },
          ].map((detail, index) => (
            <div key={index}>
              <label className="block text-gray-600 font-medium text-sm md:text-base">
                {detail.label}
              </label>
              <p className="text-gray-800 text-sm md:text-base">{detail.value}</p>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row justify-between mt-6 space-y-4 md:space-y-0">
          <button
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-4 md:px-6 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 w-full md:w-auto"
          >
            Back
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 md:px-6 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 w-full md:w-auto"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderInfo;
